import React, { useEffect, useRef } from "react";
import {
  Box,
  Card,
  Typography,
  LinearProgress,
  IconButton,
} from "@mui/material";
import { Warning, CheckCircle, Error, Close } from "@mui/icons-material";
import styles from "./styles.module.css";

const SnackBarProgresso = ({
  open,
  onClose,
  progressData,
  showLoading = false,
  onLoadingChange,
  inModal = false, // Prop para usar dentro de modal (position relative)
}) => {
  const autoCloseTimeoutRef = useRef(null);

  // ✅ CORREÇÃO: Melhorar detecção de estados
  const isError = progressData?.erro || progressData?.sucesso === false;
  const isSuccess =
    progressData?.sucesso === true ||
    (progressData?.progresso === 100 &&
      progressData?.titulo?.includes("concluído"));

  // ✅ NOVA FUNCIONALIDADE: Auto-fechar após 3 segundos em caso de erro ou sucesso
  useEffect(() => {
    // Limpar timeout anterior se existir
    if (autoCloseTimeoutRef.current) {
      clearTimeout(autoCloseTimeoutRef.current);
      autoCloseTimeoutRef.current = null;
    }

    // Se há erro ou sucesso, configurar auto-fechar após 3 segundos
    if ((isError || isSuccess) && onClose) {
      autoCloseTimeoutRef.current = setTimeout(() => {
        onClose();
      }, 3000);
    }

    // Cleanup function
    return () => {
      if (autoCloseTimeoutRef.current) {
        clearTimeout(autoCloseTimeoutRef.current);
        autoCloseTimeoutRef.current = null;
      }
    };
  }, [isError, isSuccess, onClose]);

  if (!progressData || !open) return null;

  // Cores baseadas nos padrões do Material-UI Alert variant="filled"
  const getColors = () => {
    if (isError) {
      return {
        backgroundColor: "#d32f2f", // Material-UI error filled
        borderColor: "#d32f2f",
        barColor: "#d32f2f",
        textColor: "#ffffff", // Texto branco sobre fundo escuro
        iconColor: "#ffffff",
      };
    } else if (isSuccess) {
      return {
        backgroundColor: "#2e7d32", // Material-UI success filled
        borderColor: "#2e7d32",
        barColor: "#2e7d32",
        textColor: "#ffffff", // Texto branco sobre fundo escuro
        iconColor: "#ffffff",
      };
    } else {
      return {
        backgroundColor: "#fff3cd", // Amarelo claro para progresso
        borderColor: "#ff9000",
        barColor: "#ff9000",
        textColor: "#333", // Texto escuro sobre fundo claro
        iconColor: "#ff9000",
      };
    }
  };

  const colors = getColors();

  // Ícone baseado no estado
  const getIcon = () => {
    if (isError) return <Error sx={{ color: colors.iconColor }} />;
    if (isSuccess) return <CheckCircle sx={{ color: colors.iconColor }} />;
    return <Warning sx={{ color: colors.iconColor }} />;
  };

  // Função para gerar mensagem curta e informativa
  const getMensagemCurta = () => {
    if (isError) {
      return "Erro na execução";
    }
    if (isSuccess) {
      return "Execução concluída";
    }
    
    // Mensagens curtas durante o processamento
    if (progressData.mensagem) {
      // Limitar a 40 caracteres e simplificar
      const msg = progressData.mensagem.length > 40 
        ? progressData.mensagem.substring(0, 37) + "..." 
        : progressData.mensagem;
      return msg;
    }
    
    if (progressData.titulo) {
      const msg = progressData.titulo.length > 40 
        ? progressData.titulo.substring(0, 37) + "..." 
        : progressData.titulo;
      return msg;
    }
    
    if (progressData.motorAtual && progressData.empresaAtual) {
      const msg = `${progressData.motorAtual} - ${progressData.empresaAtual}`;
      return msg.length > 40 ? msg.substring(0, 37) + "..." : msg;
    }
    
    if (progressData.motorAtual) {
      const msg = progressData.motorAtual;
      return msg.length > 40 ? msg.substring(0, 37) + "..." : msg;
    }
    
    return "Executando motores...";
  };

  return (
    <>
      <Card
        className={styles.progressCard}
        sx={{
          position: inModal ? "relative" : "fixed",
          bottom: inModal ? "auto" : 20,
          left: inModal ? "auto" : 20,
          minWidth: inModal ? "100%" : 420,
          maxWidth: inModal ? "100%" : 420,
          height: inModal ? "auto" : 80, // Altura fixa apenas quando não está no modal
          minHeight: inModal ? "auto" : 80,
          zIndex: inModal ? "auto" : 10000, // Adjusted z-index
          boxShadow: inModal ? "1px 1px 5px #c6c6c6" : 3,
          borderRadius: inModal ? "5px" : 2,
          backgroundColor: colors.backgroundColor,
          border: `1px solid ${colors.borderColor}`,
          margin: inModal ? 0 : "auto",
        }}
      >
        {/* Barra de progresso superior grudada */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            backgroundColor: colors.barColor,
            borderRadius: "8px 8px 0 0",
          }}
        >
          <LinearProgress
            variant="determinate"
            value={progressData.progresso || 0}
            sx={{
              height: "100%",
              backgroundColor: "transparent",
              "& .MuiLinearProgress-bar": {
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                borderRadius: "0 8px 0 0",
              },
            }}
          />
        </Box>

        {/* Conteúdo principal - UMA LINHA ÚNICA (Opção 1) */}
        <Box
          sx={{
            p: inModal ? "0.75rem 0.9rem" : 2,
            pt: inModal ? "calc(0.75rem + 4px)" : 3, // 4px para a barra de progresso superior
            height: inModal ? "auto" : "100%",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {/* Ícone da esquerda */}
          <Box sx={{ flexShrink: 0 }}>{getIcon()}</Box>

          {/* Mensagem curta + Porcentagem */}
          <Box sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            {/* Mensagem curta */}
            <Typography
              variant="body1"
              sx={{
                fontSize: "0.95rem",
                fontWeight: 500,
                color: colors.textColor,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              {getMensagemCurta()}
            </Typography>

            {/* Porcentagem à direita */}
            <Typography
              variant="body2"
              sx={{
                fontSize: "0.95rem",
                fontWeight: 700,
                color: colors.iconColor,
                flexShrink: 0,
              }}
            >
              {progressData.progresso || 0}%
            </Typography>
          </Box>

          {/* Ícone de fechar à direita */}
          <Box
            sx={{
              flexShrink: 0,
              width: 24,
              display: "flex",
              justifyContent: "center",
            }}
          >
            {(isError || isSuccess) && onClose ? (
              <IconButton
                size="small"
                onClick={onClose}
                sx={{
                  color: colors.iconColor,
                  "&:hover": {
                    backgroundColor: `${colors.iconColor}20`,
                  },
                }}
              >
                <Close fontSize="small" />
              </IconButton>
            ) : null}
          </Box>
        </Box>
      </Card>

      {/* Componente Loading original com opacidade reduzida */}
      {showLoading && (
        <div style={{ opacity: 0.6 }}>
          <div
            style={{
              position: inModal ? "absolute" : "fixed",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              zIndex: inModal ? "auto" : 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                width: "100px",
                height: "100px",
                border: "4px solid #f3f3f3",
                borderTop: "4px solid #18316e",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </>
  );
};

export default SnackBarProgresso;
